import { useState } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Button, Card, CardContent, cn } from '@pantry/ui';
import {
  useStorageLocations,
  useHousehold,
  type StorageLocation,
  type StorageLocationType,
} from '@pantry/api-client';
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArchiveBoxIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  DeleteStorageLocationDialog,
  StorageLocationFormDialog,
} from '../../components/storage-locations';

export const Route = createFileRoute('/_app/households_/$householdId_/storage-locations')({
  component: StorageLocationsPage,
});

/**
 * Icon map for storage location types.
 */
const LOCATION_ICONS: Record<StorageLocationType, React.ReactNode> = {
  fridge: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-6 w-6"
    >
      <path d="M5 6a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6Z" />
      <path d="M5 10h14" />
      <path d="M15 7v6" />
    </svg>
  ),
  freezer: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-6 w-6"
    >
      <path d="M12 2v20" />
      <path d="M2 12h20" />
      <path d="m4.93 4.93 4.24 4.24" />
      <path d="m14.83 9.17 4.24-4.24" />
      <path d="m4.93 19.07 4.24-4.24" />
      <path d="m14.83 14.83 4.24 4.24" />
    </svg>
  ),
  pantry: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-6 w-6"
    >
      <path d="M4 10V4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6" />
      <path d="M2 14h20" />
      <path d="M4 14v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" />
      <path d="M12 14v8" />
    </svg>
  ),
  other: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-6 w-6"
    >
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M9 3v18" />
      <path d="M15 3v18" />
      <path d="M3 9h18" />
      <path d="M3 15h18" />
    </svg>
  ),
};

function StorageLocationsPage() {
  const { t } = useLingui();
  const { householdId } = Route.useParams();

  const getStorageTypeLabel = (type: StorageLocationType): string => {
    switch (type) {
      case 'fridge':
        return t`Fridge`;
      case 'freezer':
        return t`Freezer`;
      case 'pantry':
        return t`Pantry`;
      case 'other':
        return t({ message: 'Other', context: 'storage_location_type' });
      default:
        return type;
    }
  };
  const { data: household, isLoading: householdLoading } = useHousehold(householdId);
  const {
    data: storageLocations,
    isLoading: locationsLoading,
    error,
  } = useStorageLocations(householdId);

  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState<StorageLocation | null>(null);
  const [deletingLocation, setDeletingLocation] = useState<StorageLocation | null>(null);

  const isLoading = householdLoading || locationsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-3 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Link
          to="/households/$householdId"
          params={{ householdId }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <Trans>Back to Household Settings</Trans>
        </Link>
        <Card className="rounded-xl">
          <CardContent className="py-8">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10">
                <ExclamationTriangleIcon className="h-6 w-6 text-destructive" />
              </div>
              <p className="text-destructive font-medium">
                {error.message || t`Failed to load storage locations`}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasLocations = storageLocations && storageLocations.length > 0;

  const handleEdit = (location: StorageLocation) => {
    setEditingLocation(location);
    setShowFormDialog(true);
  };

  const handleCloseForm = () => {
    setShowFormDialog(false);
    setEditingLocation(null);
  };

  return (
    <div className="space-y-6">
      {/* Back link */}
      <div>
        <Link
          to="/households/$householdId"
          params={{ householdId }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <Trans>Back to Household Settings</Trans>
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-4 items-center">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10">
            <ArchiveBoxIcon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold text-foreground">
              <Trans>Storage Locations</Trans>
            </h1>
            <p className="text-muted-foreground">
              {household?.name ? (
                <Trans>Manage storage for {household.name}</Trans>
              ) : (
                <Trans>Manage where you store food</Trans>
              )}
            </p>
          </div>
        </div>
        <Button onClick={() => setShowFormDialog(true)} className="rounded-xl h-10">
          <PlusIcon className="h-4 w-4 mr-2" />
          <Trans>Add Location</Trans>
        </Button>
      </div>

      {/* Storage locations grid */}
      {hasLocations ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {storageLocations.map((location, index) => (
            <Card
              key={location.id}
              className="rounded-xl hover:shadow-md hover:border-primary/20 transition-all duration-200 animate-in fade-in slide-in-from-bottom-2"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <CardContent className="py-5">
                <div className="flex items-center gap-4">
                  {/* Type icon */}
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    {LOCATION_ICONS[location.type]}
                  </div>

                  {/* Location info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-semibold text-foreground truncate">
                      {location.name}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{getStorageTypeLabel(location.type)}</span>
                      <span className="text-border">•</span>
                      <span>
                        <Plural value={location.itemCount ?? 0} one="# item" other="# items" />
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-shrink-0 gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(location)}
                      className="rounded-xl h-9 w-9 text-muted-foreground hover:text-foreground"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeletingLocation(location)}
                      className="rounded-xl h-9 w-9 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="rounded-2xl animate-in fade-in duration-300">
          <CardContent className="py-12">
            <div className="text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                <ArchiveBoxIcon className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-display text-xl font-semibold text-foreground">
                <Trans>No storage locations yet</Trans>
              </h3>
              <p className="mt-2 text-muted-foreground max-w-sm mx-auto">
                <Trans>
                  Add a fridge, freezer, pantry, or other location to organize your food inventory.
                </Trans>
              </p>
              <Button className="mt-6 rounded-xl h-11" onClick={() => setShowFormDialog(true)}>
                <PlusIcon className="h-4 w-4 mr-2" />
                <Trans>Add Your First Location</Trans>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form Dialog (Create/Edit) */}
      <StorageLocationFormDialog
        householdId={householdId}
        location={editingLocation}
        open={showFormDialog}
        onOpenChange={handleCloseForm}
        householdExpiryThresholdDays={household?.expiryThresholdDays}
      />

      {/* Delete Confirmation Dialog */}
      {deletingLocation && (
        <DeleteStorageLocationDialog
          householdId={householdId}
          locationId={deletingLocation.id}
          locationName={deletingLocation.name}
          itemCount={deletingLocation.itemCount ?? 0}
          open={!!deletingLocation}
          onOpenChange={(open) => !open && setDeletingLocation(null)}
        />
      )}
    </div>
  );
}
